import React, { useState, useEffect } from 'react';
import Icon from '../../components/AppIcon';
import { supabase } from '../../lib/supabase';
import { fmtMT, fmtNum, formatFortnightRange } from './utils';
import { useTranslation } from '../../hooks/useTranslation';

// Small, additive, read-only banner surfacing the most recent partial-period
// (fortnightly) release order, sourced from mi_fortnight_notices only.
// Deliberately does NOT touch mi_release_orders or any mi_v_* view/metric -
// those still reflect the latest *complete* month until both fortnights of
// a split month are combined into a single row (see project notes on the
// Sept 2026 fortnightly release). If the table is empty, missing, or the
// fetch fails for any reason, this renders nothing rather than surface an
// error - it's a nice-to-have notice, not core page functionality.
const FortnightNotice = () => {
  const { t, language } = useTranslation();
  const [notice, setNotice] = useState(null);

  useEffect(() => {
    let isMounted = true;
    (async () => {
      try {
        const { data, error } = await supabase
          .from('mi_fortnight_notices')
          .select('period_label, period_start, period_end, total_allocated_mt, mills_count, notification_no, is_active')
          .eq('is_active', true)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();
        if (error) throw error;
        if (isMounted) setNotice(data || null);
      } catch (e) {
        // Swallow - a missing/broken notice must never block the rest of the page.
        console.error('FortnightNotice: failed to load', e);
      }
    })();
    return () => { isMounted = false; };
  }, []);

  if (!notice) return null;

  const range = formatFortnightRange(notice.period_start, notice.period_end, language) || notice.period_label;

  return (
    <div
      className="flex items-start gap-3 mb-6 px-4 py-3"
      style={{
        background: 'var(--bg-surface)',
        border: '1px solid var(--info)',
        borderRadius: 'var(--radius-button)',
      }}
      role="status"
    >
      <Icon name="Megaphone" size={18} color="var(--info)" className="flex-shrink-0 mt-0.5" />
      <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
        {t('marketIntelligence.fortnightNotice', {
          defaultValue: '{{range}} published — {{total}} allocated across {{mills}} mills.',
          total: fmtMT(notice.total_allocated_mt),
          mills: fmtNum(notice.mills_count),
          range,
        })}
      </p>
    </div>
  );
};

export default FortnightNotice;
